/**
 * The quiz results' topic view: marks per lesson section, what to revise, and
 * the AI's reading of the attempt.
 *
 * The marks arrive with the last answer; the AI review is fetched afterwards
 * and takes a few seconds, so it has its own state rather than holding the
 * whole results screen back.
 */
import type { QuizAiReview, QuizTopicResult } from "../types";

export type AiReviewState = "loading" | "ready" | "off" | "failed";

export function QuizTopicSummary({
  topics,
  aiReview,
  aiState,
}: {
  topics: QuizTopicResult[];
  aiReview: QuizAiReview | null;
  aiState: AiReviewState;
}) {
  if (topics.length === 0) return null;
  const revise = topics.filter((t) => t.needs_revision);
  const reviewFor = (title: string) => aiReview?.topics.find((r) => r.title === title);

  return (
    <div className="quiz-topics">
      <section aria-labelledby="quiz-topics-heading">
        <h3 id="quiz-topics-heading">How you did by topic</h3>
        <ul className="quiz-topic-list">
          {topics.map((t) => (
            <li key={t.section_id} className="quiz-topic-row">
              <span className="quiz-topic-title">{t.title}</span>
              <span className="quiz-topic-count">
                {t.correct} of {t.total}
              </span>
              <span className="quiz-topic-bar" aria-hidden="true">
                <span
                  className={t.needs_revision ? "quiz-topic-fill quiz-topic-fill-low" : "quiz-topic-fill"}
                  style={{ width: `${Math.round(t.score * 100)}%` }}
                />
              </span>
              {t.needs_revision && <span className="quiz-topic-flag">Revise</span>}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="quiz-revise-heading">
        <h3 id="quiz-revise-heading">Topics to revise</h3>
        {revise.length === 0 ? (
          <p className="text-muted">Nothing stands out — every topic you were asked about held up.</p>
        ) : (
          <ul className="quiz-revise-list">
            {revise.map((t) => (
              <li key={t.section_id}>
                <strong>{t.title}</strong>
                {t.misconceptions.length > 0 && (
                  <p>Misunderstood: {t.misconceptions.join("; ")}</p>
                )}
                {t.missing_concepts.length > 0 && (
                  <p>Missing from your answers: {t.missing_concepts.join("; ")}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="quiz-ai-heading" className="quiz-ai-review">
        <h3 id="quiz-ai-heading">What your answers show</h3>
        {aiState === "loading" && (
          <p className="text-muted" role="status">Reading through your whole attempt…</p>
        )}
        {aiState === "off" && (
          <p className="text-muted">
            Your answers aren't sent to the AI, so there's no written review. You can change
            that on your account page.
          </p>
        )}
        {aiState === "failed" && (
          <p className="text-muted">
            The written review couldn't be made this time. Your marks above are unaffected.
          </p>
        )}
        {aiState === "ready" && aiReview && (
          <>
            {aiReview.overview && <p className="quiz-ai-overview">{aiReview.overview}</p>}
            {topics.map((t) => {
              const r = reviewFor(t.title);
              if (!r) return null;
              return (
                <article key={t.section_id} className="quiz-ai-topic">
                  <h4>{t.title}</h4>
                  {r.understanding && <p>{r.understanding}</p>}
                  {r.strengths.length > 0 && (
                    <>
                      <p className="quiz-ai-label">You understand</p>
                      <ul>{r.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </>
                  )}
                  {r.gaps.length > 0 && (
                    <>
                      <p className="quiz-ai-label">Still unclear</p>
                      <ul>{r.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
                    </>
                  )}
                  {r.advice && <p className="quiz-ai-advice">Next: {r.advice}</p>}
                </article>
              );
            })}
          </>
        )}
      </section>
    </div>
  );
}
